import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { ElfNameGenerator } from "@/components/tools/elf-name-generator/ElfNameGenerator";
import { getCanonicalUrl } from "@/utils/seoUtils";

const ElfNameGeneratorPage = () => {
  const schema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Christmas Elf Name Generator",
      url: getCanonicalUrl("/elf-name-generator"),
      description:
        "Generate playful Christmas elf names instantly for arrival letters, daily elf notes, and holiday traditions.",
      applicationCategory: "EntertainmentApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/OnlineOnly",
      },
    }),
    [],
  );

  useSEO({
    title: "Christmas Elf Name Generator | Elf Letter Bot",
    description:
      "Generate playful Christmas elf names instantly. Try festive first names, full elf names, and save favorites for your holiday traditions.",
    canonicalUrl: getCanonicalUrl("/elf-name-generator"),
    schema,
  });

  return (
    <>
      <section className="bg-elfGreen-light py-8 md:py-16">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-elfRed/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-elfRed md:px-4 md:py-1.5 md:text-sm">
            <Sparkles className="h-4 w-4" />
            Free Elf Name Generator
          </span>
          <h1 className="mt-4 text-3xl font-bold leading-tight text-foreground md:mt-6 md:text-4xl lg:text-5xl">
            Create a Christmas Elf Name in Seconds
          </h1>
          <p className="mt-3 text-base text-muted-foreground md:mt-4 md:text-lg">
            Use this quick generator to find a festive elf name for arrival letters,
            silly morning setups, and family holiday traditions.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Need ideas for what your elf should do next?{" "}
            <Link
              to="/blog/creative-holiday-elf-arrival-ideas-2026/"
              className="font-semibold text-elfRed underline underline-offset-4"
            >
              Browse arrival ideas
            </Link>
            {" "}or{" "}
            <Link
              to="/blog/free-elf-arrival-letter-templates-2026/"
              className="font-semibold text-elfRed underline underline-offset-4"
            >
              grab printable elf letters
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="bg-white py-8 md:py-16">
        <div className="container mx-auto max-w-5xl px-4">
          <ElfNameGenerator />
        </div>
      </section>
    </>
  );
};

export default ElfNameGeneratorPage;
